import { useEffect, useState } from "react";
import api, { fmtIDR } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { Card } from "@/components/ui/card";
import { RoiChart, Kpi, pct } from "@/components/reports/RoiView";
import { SalesTrend } from "@/components/reports/SalesTrend";
import { KmkTrend } from "@/components/KmkTrend";
import { Loader2, TrendingUp, Coins, LineChart } from "lucide-react";

const Head = ({ icon: Icon, title, sub }) => (
  <div className="flex items-center justify-between mb-3">
    <div><h2 className="font-head font-semibold text-lg">{title}</h2>{sub && <p className="text-xs text-muted-foreground">{sub}</p>}</div>
    <Icon className="h-5 w-5 text-muted-foreground" />
  </div>
);

export function DashboardInsights({ clientId }) {
  const { lang } = useI18n();
  const [d, setD] = useState(null);
  const yr = new Date().getFullYear();
  useEffect(() => {
    if (!clientId) return;
    setD(null);
    api.get(`/dashboard/insights?client_id=${clientId}&year=${yr}&lang=${lang}`).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
  }, [clientId, lang, yr]);
  if (!clientId) return null;
  if (!d) return <Card className="p-8 text-center text-muted-foreground" data-testid="dash-insights-loading"><Loader2 className="inline h-4 w-4 animate-spin" /></Card>;
  if (d.error) return null;
  const r = d.roi;
  return (
    <div className="space-y-6" data-testid="dash-insights">
      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="p-5" data-testid="dash-roi">
          <Head icon={TrendingUp} title={`${fl("roiTitle", lang)} ${d.year}`} sub={fl("roiFormula", lang)} />
          <div className="grid gap-3 grid-cols-3 mb-3">
            <Kpi label={fl("roiNet", lang)} value={fmtIDR(r.net_profit)} testid="dash-roi-net" tone={r.net_profit < 0 ? "text-rose-600" : ""} />
            <Kpi label={fl("roiInvested", lang)} value={fmtIDR(r.invested)} testid="dash-roi-invested" />
            <Kpi label={fl("roiYtd", lang)} value={pct(r.roi)} testid="dash-roi-value" tone={(r.roi || 0) < 0 ? "text-rose-600" : "text-emerald-700"} />
          </div>
          <RoiChart months={r.months} lang={lang} testid="dash-roi-chart" />
        </Card>
        <Card className="p-5" data-testid="dash-sales">
          <Head icon={LineChart} title={`${fl("salesTrend", lang)} ${d.year}`} />
          {d.sales.some((s) => s.total) ? <SalesTrend rows={d.sales} lang={lang} />
            : <p className="py-10 text-center text-sm text-muted-foreground" data-testid="dash-sales-empty">Belum ada penjualan tahun ini.</p>}
        </Card>
      </div>
      <Card className="p-5" data-testid="dash-kmk">
        <Head icon={Coins} title="Kurs KMK" sub="USD dan mata uang rekening valas klien" />
        <KmkTrend currencies={d.currencies} lang={lang} />
      </Card>
    </div>
  );
}
